
import React, { useState, useEffect } from "react";
import { Card, CardContent } from "@/components/ui/card";
import { useToast } from "@/hooks/use-toast";
import ImageUploadDialog from "@/components/admin/ImageUploadDialog";
import EditorToolbar from "@/components/admin/editor/EditorToolbar";
import EditorContent from "@/components/admin/editor/EditorContent";
import { createToolbarActions } from "@/components/admin/editor/editorUtils";
import { createToolbarToolActions } from "@/components/admin/editor/toolsUtils";

interface HtmlEditorProps {
  value: string;
  onChange: (value: string) => void;
  preview?: React.ReactNode;
  fullHeight?: boolean;
  className?: string;
  theme?: string;
  postId?: string;
}

const HtmlEditor: React.FC<HtmlEditorProps> = ({ 
  value, 
  onChange, 
  fullHeight = false,
  className = '',
  theme = 'default',
  postId
}) => {
  const [isFullScreen, setIsFullScreen] = useState(false);
  const [showPreview, setShowPreview] = useState(false);
  const [showImageDialog, setShowImageDialog] = useState(false);
  const { toast } = useToast();
  
  useEffect(() => {
    console.log('HtmlEditor: Theme being used:', theme);
  }, [theme]);

  // Toggle fullscreen mode without submitting the form
  const toggleFullScreen = (e: React.MouseEvent) => {
    e.preventDefault();
    e.stopPropagation();
    setIsFullScreen(!isFullScreen);
    
    // In fullscreen mode, always show the preview alongside the editor
    if (!isFullScreen) {
      setShowPreview(true);
    }
  };
  
  // Toggle preview panel in fullscreen mode
  const togglePreview = (e: React.MouseEvent) => {
    e.preventDefault();
    e.stopPropagation();
    setShowPreview(!showPreview);
  };
  
  const handleInsertImage = (imageUrl: string, altText: string, titleText: string) => {
    const titleAttr = titleText ? ` title="${titleText}"` : '';
    const imgHtml = `<img src="${imageUrl}" alt="${altText}"${titleAttr} class="blog-inline-image" style="max-width: 70%; height: auto; border-radius: 8px; display: block; margin: 1.5rem auto;" />`;
    
    const textarea = document.getElementById("html-editor") as HTMLTextAreaElement;
    if (!textarea) return;
    
    const startPos = textarea.selectionStart;
    const newValue = 
      value.substring(0, startPos) + 
      imgHtml + 
      value.substring(startPos);
    
    onChange(newValue);
  };

  const handleUndo = (e: React.MouseEvent) => {
    e.preventDefault();
    document.execCommand('undo', false);
  };

  const handleRedo = (e: React.MouseEvent) => {
    e.preventDefault();
    document.execCommand('redo', false);
  };
  
  // Create toolbar actions
  const toolbarActions = createToolbarActions(value, onChange, setShowImageDialog);
  
  // Create tool-specific actions
  const toolbarToolActions = createToolbarToolActions(value, onChange, theme, postId);
  
  return (
    <div className={`relative w-full ${isFullScreen ? 'fixed inset-0 z-50 bg-background p-6' : ''} ${fullHeight ? 'h-full' : ''}`}>
      <Card className={`overflow-hidden ${isFullScreen ? 'h-full' : fullHeight ? 'h-full' : 'min-h-[700px]'} ${className}`}>
        <EditorToolbar 
          toolbarActions={toolbarActions}
          toolbarToolActions={toolbarToolActions}
          handleUndo={handleUndo}
          handleRedo={handleRedo}
          isFullScreen={isFullScreen}
          showPreview={showPreview}
          toggleFullScreen={toggleFullScreen}
          togglePreview={togglePreview}
        />

        <CardContent className={`p-0 ${isFullScreen || fullHeight ? 'h-[calc(100%-2.5rem)]' : 'min-h-[650px]'}`}>
          <EditorContent 
            isFullScreen={isFullScreen}
            showPreview={showPreview}
            value={value}
            onChange={onChange}
            theme={theme}
          />
        </CardContent>
      </Card>
      
      <ImageUploadDialog 
        open={showImageDialog} 
        onOpenChange={setShowImageDialog}
        onImageUploaded={handleInsertImage}
      />
    </div>
  );
};

export default HtmlEditor;
